// What the Storyteller told this player: the server's sentence, plus structure where it helps
// (the Spy's Grimoire as a table, the evil team as named chips).
import type { GrimoireView, InfoEntry, PlayerView, RoleCard } from "../protocol";
import { seatName } from "../state/selectors";
import { RoleIcon } from "./RoleIcon";

export function InfoView({
  entry,
  view,
  roles,
  showStamp = true,
}: {
  entry: InfoEntry;
  view: PlayerView;
  roles: Record<string, RoleCard>;
  showStamp?: boolean;
}) {
  const p = entry.payload;
  return (
    <div className="stack-sm" style={{ textAlign: "left" }}>
      {showStamp && (
        <span className="eyebrow">
          {entry.night_number > 0 && entry.day_number < entry.night_number
            ? `Night ${entry.night_number}`
            : `Day ${entry.day_number}`}
          {" · "}
          {entry.title}
        </span>
      )}
      <p style={{ fontSize: "1.05rem" }}>{entry.text}</p>
      {p.kind === "team" && (
        <div className="row wrap">
          {p.demon_seat_id && (
            <span className="chip chip-evil">Demon: {seatName(view, p.demon_seat_id)}</span>
          )}
          {p.minion_seat_ids.map((id) => (
            <span key={id} className="chip chip-evil">
              Minion: {seatName(view, id)}
            </span>
          ))}
          {p.bluffs.map((id) => (
            <span key={id} className="chip">
              Bluff: {roles[id]?.name ?? id}
            </span>
          ))}
        </div>
      )}
      {p.kind === "grimoire" && <GrimoireTable grimoire={p.grimoire} roles={roles} />}
    </div>
  );
}

// Grimoire reminder tokens worth showing; bookkeeping ones (died today, ghost vote) are left out.
const STATUS_LABEL: Record<string, string> = {
  drunk: "drunk",
  poisoned: "poisoned",
  protected: "protected",
  red_herring: "red herring",
  ft_phantom: "Fortune Teller's phantom",
  butler_master: "Butler's master",
  virgin_spent: "ability used",
  shot_fired: "shot used",
  safe: "can't die tonight",
  execution_safe: "survives execution today",
  exorcised: "exorcised",
  grandchild: "grandchild",
  ability_spent: "ability used",
  registers_dead: "registers as dead",
  death_choice: "chose on dying",
  goon_triggered: "chosen tonight",
};

export function statusLabels(statuses: string[]): string[] {
  return statuses.flatMap((s) => (STATUS_LABEL[s] ? [STATUS_LABEL[s]] : []));
}

export function GrimoireTable({
  grimoire,
  roles,
  icons = false,
}: {
  grimoire: GrimoireView;
  roles: Record<string, RoleCard>;
  /** Only once the game is over: the Spy's copy at night stays text only (RoleIcon). */
  icons?: boolean;
}) {
  return (
    <div className="stack-sm">
      <table className="grimoire">
        <thead>
          <tr>
            <th>Player</th>
            <th>Role</th>
            <th aria-label="Status" />
          </tr>
        </thead>
        <tbody>
          {grimoire.seats.map((s) => {
            const role = roles[s.role_id];
            const believed = s.believed_role_id ? roles[s.believed_role_id] : null;
            return (
              <tr key={s.seat_id} className={s.alive ? undefined : "faint"}>
                <td>{s.name}</td>
                <td className={s.alignment === "good" ? "good" : "evil"}>
                  {icons && <RoleIcon roleId={s.role_id} width={30} />}
                  {role?.name ?? s.role_id}
                  {believed && <span className="muted small"> (thinks {believed.name})</span>}
                  {statusLabels(s.statuses).length > 0 && (
                    <span className="muted small"> · {statusLabels(s.statuses).join(", ")}</span>
                  )}
                </td>
                <td className="small">{s.alive ? "" : "dead"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {grimoire.bluffs.length > 0 && (
        <p className="small muted">
          Demon bluffs: {grimoire.bluffs.map((id) => roles[id]?.name ?? id).join(", ")}
        </p>
      )}
    </div>
  );
}
